// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import dayjs from 'dayjs';
import { HIST_VALUES } from '@shared/ui-kit/components/period-picker';
import { useState, useMemo, useCallback } from 'react';

export const usePeriodPicker = (initialState = 365) => {
  const [period, setPeriod] = useState(initialState);

  const defaultHist = useMemo(() => HIST_VALUES.get(period)!, [period]);

  const [{ end_date, start_date }, setDate] = useState(() => ({
    end_date: dayjs().format('YYYY-MM-DD'),
    start_date: dayjs().subtract(period, 'day').format('YYYY-MM-DD'),
  }));

  const days = useMemo(
    () => dayjs(end_date).diff(start_date, 'day'),
    [end_date, start_date],
  );

  const onPeriodChange = useCallback((value: number) => {
    if (value !== 0) {
      setDate({
        end_date: dayjs().format('YYYY-MM-DD'),
        start_date: dayjs().subtract(value, 'day').format('YYYY-MM-DD'),
      });
    }

    setPeriod(value);
  }, []);

  const onDateChange = useCallback((startDate: Date, endDate: Date) => {
    setDate({
      start_date: dayjs(startDate).format('YYYY-MM-DD'),
      end_date: dayjs(endDate).format('YYYY-MM-DD'),
    });
  }, []);

  return {
    defaultHist,
    end_date,
    onDateChange,
    onPeriodChange,
    period: days,
    start_date,
  } as const;
};
